import React from 'react';
import {
  Box,
  Stack,
  Text,
  Group,
  Badge,
  Paper,
  ThemeIcon,
  Tooltip,
  Button
} from '@mantine/core';
import {
  IconSquare,
  IconCircle,
  IconAlertTriangle,
  IconX,
  IconFilterOff
} from '@tabler/icons-react';

interface LegendItem {
  id: string;
  label: string;
  color: string;
  icon?: React.ReactNode;
  description?: string;
  count?: number;
}

interface FloorPlanLegendProps {
  items: LegendItem[];
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  compact?: boolean;
  showCounts?: boolean;
  onItemClick?: ((itemId: string) => void) | undefined;
  className?: string;
  /**
   * Keep legend visible across all views (Grid and Floor Plan)
   * @default true
   */
  alwaysVisible?: boolean;
  /**
   * PHASE 4: Active filter IDs for styling
   * Per Spec Section 3: Clickable chips with multi-select
   */
  activeFilters?: string[];
  /**
   * PHASE 4: Callback to clear all legend filters
   */
  onClearFilters?: (() => void) | undefined;
}

/**
 * FloorPlanLegend - Always-visible legend for floor plan states
 * 
 * IMPORTANT: This legend shows ROOM-LEVEL statuses (entire room state),
 * not cell-level statuses. Colors must match FloorPlanCanvas ROOM_COLORS.
 * 
 * Room Status Definitions:
 * - Available (#e8f5e8): Room is available for assignment
 * - Occupied (#ffeaa7): Room has one or more assignments
 * - Blocked (#fab1a0): Room is temporarily blocked (maintenance/cleaning)
 * - Inactive (#ddd): Room is out of service
 * - Conflict (#fa5252): Room has scheduling conflicts
 * 
 * Features:
 * - Configurable position (bottom-right by default)
 * - Compact mode for space-constrained layouts
 * - Click interactions for filtering
 * - Count indicators for each state
 * - Responsive design
 * - Accessibility support with ARIA labels
 * 
 * @created 09-10-2025
 * @updated 14-10-2025 - Standardized colors to match FloorPlanCanvas
 */
const FloorPlanLegend: React.FC<FloorPlanLegendProps> = ({
  items,
  position = 'bottom-right',
  compact = false,
  showCounts = true,
  onItemClick,
  className,
  alwaysVisible = true,
  activeFilters = [],
  onClearFilters
}) => {
  // If alwaysVisible is false and no items provided, hide legend
  if (!alwaysVisible && items.length === 0) {
    return null;
  }
  const positionStyles = {
    'bottom-right': {
      position: 'absolute' as const,
      bottom: 16,
      right: 16
    },
    'bottom-left': {
      position: 'absolute' as const,
      bottom: 16,
      left: 16
    },
    'top-right': {
      position: 'absolute' as const,
      top: 16,
      right: 16
    },
    'top-left': {
      position: 'absolute' as const,
      top: 16,
      left: 16
    }
  };

  const defaultLegendItems: LegendItem[] = [
    {
      id: 'available',
      label: 'Available',
      color: '#e8f5e8',
      icon: <IconSquare size={12} />,
      description: 'Room is available for assignment'
    },
    {
      id: 'occupied',
      label: 'Occupied',
      color: '#ffeaa7',
      icon: <IconCircle size={12} />,
      description: 'Room has an assignment'
    },
    {
      id: 'blocked',
      label: 'Blocked',
      color: '#fab1a0',
      icon: <IconAlertTriangle size={12} />,
      description: 'Room is temporarily blocked'
    },
    {
      id: 'inactive',
      label: 'Inactive',
      color: '#ddd',
      icon: <IconX size={12} />,
      description: 'Room is out of service'
    },
    {
      id: 'conflict',
      label: 'Conflict',
      color: '#fa5252',
      icon: <IconAlertTriangle size={12} />,
      description: 'Room has scheduling conflicts'
    }
  ];

  const legendItems = items.length > 0 ? items : defaultLegendItems;

  if (compact) {
    return (
      <Box
        style={positionStyles[position]}
        className={className || ''}
      >
        <Paper
          p="xs"
          radius="md"
          shadow="sm"
          withBorder
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
        >
          <Group gap="xs" align="center">
            {legendItems.map((item) => (
              <Tooltip
                key={item.id}
                label={item.description || item.label}
                position="top"
              >
                <Group
                  gap="xs"
                  style={{ cursor: onItemClick ? 'pointer' : 'default' }}
                  onClick={() => onItemClick?.(item.id)}
                >
                  <ThemeIcon
                    size="xs"
                    color={item.color}
                    variant="filled"
                    style={{ borderRadius: '2px' }}
                  >
                    {item.icon}
                  </ThemeIcon>
                  <Text size="xs" fw={500}>
                    {item.label}
                  </Text>
                  {showCounts && item.count !== undefined && (
                    <Badge size="xs" variant="light" color="gray">
                      {item.count}
                    </Badge>
                  )}
                </Group>
              </Tooltip>
            ))}
          </Group>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      style={positionStyles[position]}
      className={className || ''}
    >
      <Paper
        p="md"
        radius="md"
        shadow="md"
        withBorder
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
      >
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={600} c="dimmed">
              Legend
            </Text>
            
            {/* PHASE 4: Clear Filters Button - Per Spec Section 3 */}
            {activeFilters.length > 0 && onClearFilters && (
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={<IconFilterOff size={12} />}
                onClick={onClearFilters}
                style={{ padding: '4px 8px', height: 'auto' }}
              >
                Clear
              </Button>
            )}
          </Group>
          
          {legendItems.map((item) => {
            const isActive = activeFilters.includes(item.id);
            
            return (
              <Group
                key={item.id}
                gap="sm"
                justify="space-between"
                style={{ 
                  cursor: onItemClick ? 'pointer' : 'default',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  backgroundColor: isActive ? 'rgba(34, 139, 230, 0.1)' : 'transparent',
                  border: isActive ? '1px solid rgba(34, 139, 230, 0.3)' : '1px solid transparent',
                  transition: 'all 150ms ease-in-out'
                }}
                onClick={() => onItemClick?.(item.id)}
              >
                <Group gap="sm">
                  <ThemeIcon
                    size="sm"
                    color={item.color}
                    variant="filled"
                    style={{ borderRadius: '4px' }}
                  >
                    {item.icon}
                  </ThemeIcon>
                  <Text 
                    size="sm" 
                    fw={isActive ? 600 : 500}
                    c={isActive ? 'blue' : 'dark'}
                  >
                    {item.label}
                  </Text>
                </Group>
                
                {showCounts && item.count !== undefined && (
                  <Badge 
                    size="sm" 
                    variant={isActive ? 'filled' : 'light'} 
                    color={isActive ? 'blue' : 'gray'}
                  >
                    {item.count}
                  </Badge>
                )}
              </Group>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
};

export default FloorPlanLegend;
